import { useMemo } from 'react';
import { useMyDataQuery } from '@/generated/graphql';
import { getRolePermissions, RolePermissions, RoleValue } from '@/constants/roles';

export interface UserRoleData {
  role: RoleValue | undefined;
  isMaster: boolean;
  permissions: RolePermissions;
  isLoading: boolean;
}

/**
 * Hook to get the current user's role and permissions
 * Uses myData query to get subscription info and determine the user's role
 */
export const useUserRole = (): UserRoleData => {
  const { data: myData, loading } = useMyDataQuery();

  const roleData = useMemo(() => {
    const userData = myData?.myData;
    const userEmail = userData?.email;

    // Parse subscription data - it might be a JSON string or an object
    let subscription: any = null;
    if (userData?.subscription) {
      try {
        subscription = typeof userData.subscription === 'string'
          ? JSON.parse(userData.subscription)
          : userData.subscription;
      } catch (error) {
        console.error('Error parsing subscription data:', error);
      }
    }

    const masterEmail = subscription?.master?.email;

    // Check if current user is the master (plan owner)
    const isMaster = Boolean(userEmail && masterEmail && userEmail === masterEmail);

    // Get the user's role directly from their user data
    // The role field is available on the user object itself
    const userRole = userData?.role as RoleValue | undefined;

    // Get permissions based on role and master status
    const permissions = getRolePermissions(userRole, isMaster);

    return {
      role: userRole,
      isMaster,
      permissions,
      isLoading: loading,
    };
  }, [myData, loading]);

  return roleData;
};

export default useUserRole;
